import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["acceptanceMark", "aside", "avatar", "content", "contentSelectable", "focused", "hovered", "icon", "id", "inlineContent", "isObfuscated", "keyboardFocused", "linkLabel", "omitFirstLine", "omitLastLine", "onBlur", "onClick", "onFocus", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPress", "onPressIn", "onPressOut", "onTouchEnd", "onTouchStart", "pressed", "previewLabel", "sandwich", "testID", "title", "UNSAFE_style", "UNSAFE_containerProps"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import reformatObfuscation from '@bubble-ui/util-reformat-obfuscation';
import Badge from '@bubble-ui/molecule-badge';
import LineBreak from '@bubble-ui/primitive-line-break';
import Text from '@bubble-ui/primitive-text';
import View from '@bubble-ui/primitive-view';
import Touchable from '@bubble-ui/primitive-touchable';
import { isNative } from '@bubble-ui/util-platform';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import usePropDeprecationWarning from '@bubble-ui/hook-use-prop-deprecation-warning';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { defaultDesignTokens } from './defaultDesignTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { getIds } from './getIds';
import { PreviewLink } from './PreviewLink';
var Preview = _ref => {
  var {
      acceptanceMark,
      aside,
      avatar,
      content: contentProp,
      contentSelectable = false,
      focused,
      hovered,
      icon,
      id,
      inlineContent: inlineContentProp,
      isObfuscated,
      keyboardFocused,
      linkLabel,
      omitFirstLine,
      omitLastLine,
      onBlur,
      onClick,
      onFocus,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPress: onPressProp,
      onPressIn,
      onPressOut,
      onTouchEnd,
      onTouchStart,
      pressed,
      previewLabel,
      sandwich,
      testID,
      title: titleProp,
      UNSAFE_style,
      UNSAFE_containerProps
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  usePropDeprecationWarning({
    componentName: 'Preview',
    propName: 'onClick',
    shouldWarn: onClick !== undefined,
    removalDate: 'v6.0.0',
    useInstead: 'onPress'
  });
  usePropDeprecationWarning({
    componentName: 'Preview',
    propName: 'sandwich',
    shouldWarn: sandwich !== undefined,
    removalDate: 'v6.0.0'
  });
  usePropDeprecationWarning({
    componentName: 'Preview',
    propName: 'omitFirstLine',
    shouldWarn: omitFirstLine !== undefined,
    removalDate: 'v6.0.0'
  });
  usePropDeprecationWarning({
    componentName: 'Preview',
    propName: 'omitLastLine',
    shouldWarn: omitLastLine !== undefined,
    removalDate: 'v6.0.0'
  });
  usePropsSpreadingDeprecationWarning({
    componentName: 'Preview',
    props,
    useInstead: 'UNSAFE_containerProps',
    removalDate: 'v7.0.0'
  });
  var designVersion = useDesignVersion();
  var designContext = useDesignContext(defaultDesignTokens);
  var content = isObfuscated ? contentProp === null || contentProp === void 0 ? void 0 : contentProp.map(reformatObfuscation) : contentProp;
  var singleLine = (!content || content.length === 0) && !previewLabel;
  var hasContent = content !== undefined && content.length > 0;
  var ids = getIds(id);
  var title = isObfuscated ? reformatObfuscation(titleProp) : titleProp;
  var inlineContent = isObfuscated ? reformatObfuscation(inlineContentProp) : inlineContentProp;
  var onPress = onPressProp || onClick;
  var [interactiveState, eventHandlers] = useInteractiveStateCombined({
    focused,
    hovered,
    keyboardFocused,
    onBlur,
    onFocus,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed
  });
  var {
    asideStyle,
    acceptanceMarkStyle,
    avatarStyle,
    contentStyle,
    iconStyle,
    inlineContentTextStyle,
    linkStyle,
    rootStyle,
    touchableStyle,
    previewTopStyle,
    previewLabelStyle,
    titleWrapperStyle,
    previewGrowAndShrink,
    titleStyle,
    paragraphSecondaryCorrectFootprintStyle,
    paragraphSecondaryCorrectFootprintLightStyle,
    spacerHorizontalMediumStyle,
    spacerVerticalExtraSmallStyle,
    spacerVerticalSmallStyle
    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
  } = useStyleSheet(Preview, getDefaultStyleSheet, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, props), {}, {
    acceptanceMark,
    aside,
    avatar,
    content,
    contentSelectable,
    designContext,
    designVersion,
    hasContent,
    icon,
    id,
    isObfuscated
  }, interactiveState), {}, {
    inlineContent,
    linkLabel,
    omitFirstLine,
    omitLastLine
  }, eventHandlers), {}, {
    onPress,
    previewLabel,
    sandwich,
    singleLine,
    testID,
    title,
    UNSAFE_style,
    UNSAFE_containerProps
  }));
  var titleElement = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    id: ids.title,
    numberOfLines: 0,
    tag: "h2",
    style: titleStyle
  }, title), inlineContent && /*#__PURE__*/React.createElement(Text, {
    id: ids.inlineContentTitle,
    style: inlineContentTextStyle
  }, inlineContent));
  var all = /*#__PURE__*/React.createElement(React.Fragment, null, sandwich || /*#__PURE__*/React.createElement(View, {
    style: spacerHorizontalMediumStyle
  }), /*#__PURE__*/React.createElement(View, {
    style: contentStyle
  }, !singleLine && /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalExtraSmallStyle
  }), /*#__PURE__*/React.createElement(View, {
    style: {
      flexDirection: 'row',
      alignItems: !singleLine ? undefined : 'center'
    }
  }, aside != null && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    id: ids.asideWrapper,
    style: asideStyle
  }, aside), /*#__PURE__*/React.createElement(View, {
    style: spacerHorizontalMediumStyle
  })), avatar != null && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    id: ids.avatarWrapper,
    style: avatarStyle
  }, avatar), /*#__PURE__*/React.createElement(View, {
    style: spacerHorizontalMediumStyle
  })), acceptanceMark != null && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    id: ids.acceptanceMarkWrapper,
    style: acceptanceMarkStyle
  }, acceptanceMark), /*#__PURE__*/React.createElement(View, {
    style: spacerHorizontalMediumStyle
  })), icon != null && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    id: ids.iconWrapper,
    style: iconStyle
  }, icon), /*#__PURE__*/React.createElement(View, {
    style: spacerHorizontalMediumStyle
  })), /*#__PURE__*/React.createElement(View, {
    style: previewGrowAndShrink
  }, !singleLine && /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalExtraSmallStyle
  }), /*#__PURE__*/React.createElement(View, {
    style: previewTopStyle
  }, previewLabel != null ? /*#__PURE__*/React.createElement(View, {
    id: ids.label,
    style: previewLabelStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(Badge, {
    backgroundColorToken: "preview/badge-background-color",
    textColorToken: "preview/badge-text-color"
  }, previewLabel))) : /*#__PURE__*/React.createElement(View, {
    id: ids.header,
    style: titleWrapperStyle
  }, titleElement), linkLabel && /*#__PURE__*/React.createElement(View, {
    id: ids.linkWrapper,
    style: linkStyle
  }, /*#__PURE__*/React.createElement(Text, _extends({}, isNative ? {
    numberOfLines: 0
  } : {}, {
    tag: "paragraph",
    style: paragraphSecondaryCorrectFootprintStyle,
    id: ids.linkParagraph
  }), /*#__PURE__*/React.createElement(PreviewLink, {
    hovered: interactiveState.hovered,
    id: ids.link,
    pressed: interactiveState.pressed
  }, linkLabel)))), previewLabel != null && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalSmallStyle
  }), /*#__PURE__*/React.createElement(View, {
    id: ids.header,
    style: titleWrapperStyle
  }, titleElement)), hasContent && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalExtraSmallStyle
  }), /*#__PURE__*/React.createElement(Text, _extends({}, isNative ? {
    numberOfLines: 0
  } : {}, {
    selectable: contentSelectable,
    tag: "paragraph",
    style: paragraphSecondaryCorrectFootprintLightStyle,
    id: ids.contentParagraph
  }), content === null || content === void 0 ? void 0 : content.map((line, index) => {
    var isLast = index === content.length - 1;
    return /*#__PURE__*/React.createElement(Text, {
      id: ids.line(index),
      key: index,
      style: paragraphSecondaryCorrectFootprintLightStyle
    }, line, !isLast && /*#__PURE__*/React.createElement(LineBreak, null));
  }))))), hasContent ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalExtraSmallStyle
  }), /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalSmallStyle
  })) : previewLabel ?
  /*#__PURE__*/
  // not sure if realistic case, but this avoids screenshot changes
  React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalExtraSmallStyle
  }), /*#__PURE__*/React.createElement(View, {
    style: spacerVerticalExtraSmallStyle
  })) : undefined), sandwich || /*#__PURE__*/React.createElement(View, {
    style: spacerHorizontalMediumStyle
  }));
  var _rootStyle = _objectSpread(_objectSpread({}, rootStyle), UNSAFE_style);
  return onPress && linkLabel ? /*#__PURE__*/React.createElement(Touchable, _extends({
    id: ids.touchable,
    testID: testID
  }, UNSAFE_containerProps, props, {
    onPress: onPress,
    style: touchableStyle
  }, eventHandlers), /*#__PURE__*/React.createElement(View, {
    id: ids.root,
    style: _rootStyle
  }, all)) : /*#__PURE__*/React.createElement(View, _extends({
    id: ids.root,
    testID: testID,
    style: _rootStyle
  }, props), all);
};
Preview.displayName = 'Preview';
export default withProvisionalOverrideFromContext(Preview);
//# sourceMappingURL=index.js.map